import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Plus, Bell } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { gapi, fmtDateTime } from "../api";
import { useGAuth } from "../GAuth";

export function GlobalSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const box = useRef(null);
  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return; }
    const t = setTimeout(() => gapi.get("/search", { params: { q } }).then((r) => setRes(r.data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);
  const go = (path) => { setQ(""); setRes(null); navigate(path); };
  const empty = res && !res.contatti.length && !res.immobili.length && !res.richieste.length;
  return (
    <div className="relative flex-1 max-w-md" ref={box}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-anthracite/35" />
      <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Escape" && setQ("")} placeholder="Cerca contatti, immobili, codici…"
        className="w-full h-10 rounded-full border border-border-subtle bg-white pl-9 pr-4 text-sm outline-none focus:border-brand" data-testid="g-global-search" />
      {res && (
        <div className="absolute z-50 mt-2 w-full sm:w-[440px] rounded-2xl border border-border-subtle bg-white shadow-xl p-2 text-sm max-h-[70vh] overflow-auto" data-testid="g-search-results">
          {empty && <p className="p-3 text-anthracite/45">Nessun risultato.</p>}
          {[["contatti", "Contatti", (x) => `/gestionale/contatti/${x.id}`, (x) => x.displayName],
            ["immobili", "Immobili", (x) => `/gestionale/immobili/${x.id}`, (x) => `${x.codiceRif} · ${x.titolo}`],
            ["richieste", "Richieste", (x) => `/gestionale/richieste/${x.id}`, (x) => `${x.contattoNome} · ${x.tipo}`]].map(([k, label, to, text]) => res[k].length > 0 && (
            <div key={k} className="mb-1">
              <p className="px-3 pt-2 text-[10px] uppercase tracking-widest text-anthracite/40">{label}</p>
              {res[k].map((x) => <button key={x.id} onClick={() => go(to(x))} className="block w-full text-left px-3 py-2 rounded-lg hover:bg-accent-subtle" data-testid={`g-search-${k}-item`}>{text(x)}</button>)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function QuickAdd() {
  const navigate = useNavigate();
  const { can } = useGAuth();
  const items = [
    ["contatti", "Contatto", "/gestionale/contatti/nuovo"],
    ["immobili", "Immobile", "/gestionale/immobili/nuovo"],
    ["richieste", "Richiesta di ricerca", "/gestionale/richieste/nuova"],
    ["agenda", "Appuntamento", "/gestionale/agenda"],
    ["attivita", "Attività", "/gestionale/attivita"],
  ].filter(([r]) => can(r, "creare"));
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="grid place-items-center h-10 w-10 sm:w-auto sm:px-4 rounded-full bg-brand text-white text-sm font-semibold gap-2 sm:flex" data-testid="g-quick-add"><Plus className="h-4 w-4" /><span className="hidden sm:inline">Nuovo</span></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {items.map(([r, l, to]) => <DropdownMenuItem key={r} onSelect={() => navigate(to)} data-testid={`g-quick-add-${r}`}>{l}</DropdownMenuItem>)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function NotificationsBell() {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], nonLette: 0 });
  const load = () => gapi.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);
  const open = (v) => { if (v && data.nonLette) gapi.post("/notifications/read").then(() => setTimeout(load, 3000)); };
  return (
    <Popover onOpenChange={open}>
      <PopoverTrigger asChild>
        <button className="relative grid place-items-center h-10 w-10 rounded-full border border-border-subtle bg-white" aria-label="Notifiche" data-testid="g-notifications">
          <Bell className="h-4 w-4" />
          {data.nonLette > 0 && <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-red-600 text-white text-[10px] font-bold grid place-items-center px-1" data-testid="g-notifications-count">{data.nonLette}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="max-h-96 overflow-auto divide-y divide-border-subtle" data-testid="g-notifications-list">
          {!data.items.length && <p className="p-4 text-sm text-anthracite/45">Nessuna notifica.</p>}
          {data.items.map((n) => (
            <button key={n.id} onClick={() => n.link && navigate(n.link)} className={`block w-full text-left p-3 text-sm hover:bg-accent-subtle ${n.letta ? "" : "bg-sky-50/60"}`}>
              <b>{n.titolo}</b><br /><span className="text-anthracite/60">{n.testo}</span><br /><span className="text-[11px] text-anthracite/40">{fmtDateTime(n.at)}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
